"use client";

import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from "axios";
import { toast } from "react-toastify";

const SUCCESS_MESSAGES: Record<string, string> = {
  post: "Request submitted successfully.",
  put: "Request updated successfully.",
  patch: "Request updated successfully.",
  delete: "Request deleted successfully.",
};

function getMethodLabel(method?: string) {
  return (method || "get").toLowerCase();
}

function isToastableMethod(method?: string) {
  const label = getMethodLabel(method);
  return label === "post" || label === "put" || label === "patch" || label === "delete";
}

function getSuccessMessage(response: AxiosResponse) {
  const configMessage = response.config.toast?.successMessage;
  if (configMessage) return configMessage;

  const data = response.data;
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }

  return SUCCESS_MESSAGES[getMethodLabel(response.config.method)] || "Request submitted successfully.";
}

function getErrorMessage(error: AxiosError) {
  const configMessage = error.config?.toast?.errorMessage;
  if (configMessage) return configMessage;

  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (
    data &&
    typeof data === "object" &&
    "message" in data &&
    typeof data.message === "string" &&
    data.message.trim()
  ) {
    return data.message.trim();
  }

  return error.message || "Something went wrong. Please try again.";
}

function shouldShowSuccessToast(response: AxiosResponse) {
  if (!isToastableMethod(response.config.method)) {
    return response.config.toast?.success === true;
  }

  return response.config.toast?.success !== false;
}

function shouldShowErrorToast(error: AxiosError) {
  if (!isToastableMethod(error.config?.method)) {
    return error.config?.toast?.error === true;
  }

  return error.config?.toast?.error !== false;
}

export function attachToastInterceptors(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => {
      if (typeof window !== "undefined" && shouldShowSuccessToast(response)) {
        toast.success(getSuccessMessage(response));
      }

      return response;
    },
    (error: AxiosError) => {
      if (typeof window !== "undefined" && shouldShowErrorToast(error)) {
        toast.error(getErrorMessage(error));
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }

    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message.trim()
    ) {
      return data.message.trim();
    }

    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
